import React from 'react';
import { Box, Checkbox, FormControlLabel, TextField } from '@mui/material';
import dayjs from 'dayjs';
import type { SchedulingItem } from '@/types';
import { isHazMat } from '@/services/scheduling/schedulingService';

interface FieldProps {
  id: string;
  label: string;
  value?: string | number | null;
  multiline?: boolean;
}

const Field: React.FC<FieldProps> = ({ id, label, value, multiline }) => (
  <TextField
    id={id}
    label={label}
    value={value ?? ''}
    disabled
    multiline={multiline}
    maxRows={7}
    size="small"
    variant="standard"
    fullWidth
    slotProps={{ inputLabel: { shrink: true } }}
  />
);

const Column: React.FC<{ flex?: number; children: React.ReactNode }> = ({ flex = 1, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex, minWidth: 140 }}>{children}</Box>
);

export const ExpandedSchedulingRow: React.FC<{ item: SchedulingItem }> = ({ item }) => {
  const entryDate = item.orderPlacedDate && dayjs(item.orderPlacedDate).isValid()
    ? dayjs(item.orderPlacedDate).format('MM/DD/YY')
    : '';

  return (
    <Box sx={{ display: 'flex', gap: 4, p: 2, flexWrap: 'wrap', backgroundColor: '#fafafa' }} onClick={(event) => event.stopPropagation()}>
      <Column>
        <Field id="qa-expandedRow-orderCreator-ipt" label="Order Creator" value={item.orderCreator} />
        <Field id="qa-expandedRow-entryDate-ipt" label="Entry Date" value={entryDate} />
      </Column>

      <Column>
        <Field id="qa-expandedRow-plantDescription-ipt" label="Supply Point Name" value={item.plantDescription} />
        <Field id="qa-expandedRow-plantAddress-ipt" label="Supply Point Address" value={item.plantAddress} />
        <Field id="qa-expandedRow-plantRegion-ipt" label="Supply Point Region" value={item.plantRegion} />
        <Field id="qa-expandedRow-plantCity-ipt" label="Supply Point City" value={item.plantCity} />
        <Field id="qa-expandedRow-plantPostalCode-ipt" label="Supply Point Postal Code" value={item.plantPostalCode} />
        <Field id="qa-expandedRow-plantCountry-ipt" label="Supply Point Country" value={item.plantCountry} />
      </Column>

      <Column>
        <Field id="qa-expandedRow-shipToDescription-ipt" label="Ship-to Description" value={item.shipToDescription} />
        <Field id="qa-expandedRow-shipToRegion-ipt" label="Ship-to Region" value={item.shipToRegion} />
        <Field id="qa-expandedRow-shipToCity-ipt" label="Ship-to City" value={item.shipToCity} />
        <Field id="qa-expandedRow-shipToPostalCode-ipt" label="Ship-to Postal Code" value={item.shipToPostalCode} />
        <Field id="qa-expandedRow-shipToCountry-ipt" label="Ship-to Country" value={item.shipToCountry} />
      </Column>

      <Column>
        <Field id="qa-expandedRow-routeDescription-ipt" label="Route Description" value={item.routeDescription} />
        <Field id="qa-expandedRow-materialDescription-ipt" label="Material Description" value={item.materialDescription} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Field id="qa-expandedRow-salesQuantity-ipt" label="Order Qty" value={item.salesQuantity} />
          <Field id="qa-expandedRow-salesUOM-ipt" label="Order UoM" value={item.salesUnitOfMeasure} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Field
            id="qa-expandedRow-deliveryQuantity-ipt"
            label="Shipment Qty"
            value={item.deliveryQuantity ?? item.loadedQuantity}
          />
          <Field
            id="qa-expandedRow-deliveryUnitOfMeasure-ipt"
            label="Shipment UoM"
            value={item.deliveryUnitOfMeasure ?? item.unitOfMeasure}
          />
        </Box>
        <FormControlLabel
          control={<Checkbox id="qa-expandedRow-hazmat-chb" disabled checked={isHazMat(item.hazMat)} />}
          label="Hazmat"
        />
      </Column>

      <Column flex={2}>
        <Field id="qa-expandedRow-TGRADR-txt" label="TGR/ADR" value={item.tgrAdr} multiline />
        <Field id="qa-expandedRow-shippingHaulierText-txt" label="Shipping/Haulier Text" value={item.shippingHaulierText} multiline />
      </Column>
    </Box>
  );
};
